/**
 * JustificationHistory — popover-style expandable card showing the full
 * audit trail of clinical justifications for one soft-warning rule on one
 * field. Rendered inline under the field in approver / read-only views.
 *
 * Reads from `submissions.soft_warning_flags[i].history[]` (see
 * `soft-warning-lifecycle.ts`). The array is append-only; each entry is a
 * prior justification that was superseded (with `dropped_at` timestamp).
 *
 * The CURRENT justification is shown elsewhere by the form renderer.
 * This component only displays the past entries.
 */
import { useState } from 'react'
import { Clock, ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import type { SoftWarningFlag } from '@/lib/form-engine'
import dayjs from 'dayjs'

export interface JustificationHistoryProps {
  flag: SoftWarningFlag | undefined
  /**
   * Optional map of userId → display name so we can render "نام پزشک" rather
   * than the raw UUID. If unset, falls back to UUID truncated.
   */
  userNames?: Record<string, string>
}

export function JustificationHistory({ flag, userNames }: JustificationHistoryProps) {
  const [open, setOpen] = useState(false)

  if (!flag || !flag.history || flag.history.length === 0) return null

  const formatUser = (uid: string) =>
    userNames?.[uid] ?? uid.slice(0, 8)

  return (
    <div className="mt-1.5 rounded-md border border-border bg-muted/20 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-2.5 py-1.5 flex items-center gap-1.5 text-[10px] text-muted-foreground hover:bg-muted/40 transition-colors"
      >
        <Clock className="w-3 h-3" />
        <span className="flex-1 text-start">
          {flag.history.length.toLocaleString('fa-IR')} نسخه قبلی این توجیه
        </span>
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {open && (
        <ul className="divide-y divide-border border-t border-border">
          {flag.history.map((h, idx) => (
            <li key={idx} className="px-2.5 py-1.5 space-y-0.5">
              <p className={cn('text-xs text-foreground whitespace-pre-wrap')}>
                {h.justification || <span className="italic text-muted-foreground">(خالی)</span>}
              </p>
              <p className="text-[10px] text-muted-foreground flex items-center gap-1.5">
                <span>توسط {formatUser(h.justified_by)}</span>
                <span>·</span>
                <span>{dayjs(h.justified_at).format('YYYY/MM/DD HH:mm')}</span>
                {h.dropped_at && (
                  <>
                    <span>·</span>
                    <span>تا {dayjs(h.dropped_at).format('YYYY/MM/DD HH:mm')}</span>
                  </>
                )}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
